import type { IDataSourceItem } from "./types";
import { StaticImageData } from "next/image";

import { DataSourceCard } from "./DataSourceCard";
import { ConnectorFlow } from "./ConnectorFlow";

import { DEFAULT_ITEMS, DEFAULT_LOGOS, VARIANT_TO_ICON_KEY, WEBSITE_KEYWORDS, getDefaultIcon } from "./constants";

interface Props {
    data: IDataSourceItem[];
}

export const DataSources = (props: Props) => {
    const rawItems = Array.isArray(props.data) && props.data.length > 0 ? props.data?.filter(Boolean) : DEFAULT_ITEMS;

    const getWebsiteIconKey = (label: string): string | null => {
        const lowerLabel = label.toLowerCase();
        for (const [keyword, iconKey] of WEBSITE_KEYWORDS) {
            if (lowerLabel.includes(keyword.toLowerCase())) return iconKey;
        }
        return null;
    };

    // Separate disabled and non-disabled items
    const nonDisabled = rawItems.filter(item => !item.disabled);
    const disabled = rawItems.filter(item => item.disabled);

    // Ensure 'audience-pro' is at position 4 (5th item, 0-indexed) among non-disabled
    const audiencePro = nonDisabled.find(item => item.id === 'audience-pro');
    if (audiencePro) {
        const others = nonDisabled.filter(item => item.id !== 'audience-pro');
        const insertIndex = Math.min(4, others.length);
        others.splice(insertIndex, 0, audiencePro);
        nonDisabled.length = 0; // clear
        nonDisabled.push(...others);
    }

    // Combine non-disabled first, then disabled
    const items = [...nonDisabled, ...disabled];

    return (
        <div className="mt-4 w-full h-full mx-auto flex justify-center">
            <div className="overflow-auto">
                <div className="flex gap-6">
                    {items.map((item, i) => {
                        const variantName = (item.id === 'crm' || item.id === 'dms' || item.id === 'website') ? item.label : null;
                        const iconKey = variantName ? (item.id === 'website' ? getWebsiteIconKey(variantName) || variantName : VARIANT_TO_ICON_KEY[variantName] || VARIANT_TO_ICON_KEY[variantName.toLowerCase()] || variantName) : item.id;
                        
                        // Use custom default icons for CRM, DMS, and Website only if no specific variant icon exists
                        let icon: React.ReactNode | string | StaticImageData;
                        let fallbackIcon: React.ReactNode | undefined;
                        const logoExists = DEFAULT_LOGOS[iconKey];
                        
                        if ((item.id === 'crm' || item.id === 'dms' || item.id === 'website') && !logoExists) {
                            icon = getDefaultIcon(item.id);
                        } else {
                            icon = DEFAULT_LOGOS[iconKey] || getDefaultIcon(item.id);
                            // Set fallback icon for CRM, DMS, and Website in case the image fails to load
                            if (item.id === 'crm' || item.id === 'dms' || item.id === 'website') {
                                fallbackIcon = getDefaultIcon(item.id);
                            }
                        }
                        
                        const { icon: _, ...itemProps } = item;
                        return (
                            <DataSourceCard
                                key={item.id}
                                style={{
                                    opacity: 0,
                                    animation: `fadeIn 0.5s ease-in-out ${i * 0.08}s forwards`,
                                }}
                                icon={icon}
                                fallbackIcon={fallbackIcon}
                                {...itemProps}
                            />
                        );
                    })}
                </div>
                <ConnectorFlow items={items} />
            </div>
        </div>
    );
};
